/**
 * Pure presentation helpers for the Featured Match card.
 * These reuse the existing score/result logic from the codebase but are
 * extracted here so the card components stay small and the logic is testable.
 *
 * IMPORTANT: These helpers only READ existing match data. They never alter
 * scoring, Indoor wicket penalties, strike rotation, or match lifecycle.
 */

export function getInningsScoreFor(matchData: any): { score: string; overs: string; crr: string } {
  if (!matchData) return { score: "0/0", overs: "0.0", crr: "0.00" };
  const index = typeof matchData.currentInnings === "number" ? matchData.currentInnings : 0;
  const innings = matchData.innings?.[index];
  if (!innings) return { score: "0/0", overs: "0.0", crr: "0.00" };
  const runs = innings.totalRuns || 0;
  const wickets = innings.wickets || 0;
  const totalBalls = innings.totalBalls || 0;
  const overs = `${Math.floor(totalBalls / 6)}.${totalBalls % 6}`;
  const crr = totalBalls > 0 ? ((runs * 6) / totalBalls).toFixed(2) : "0.00";
  return { score: `${runs}/${wickets}`, overs, crr };
}

/**
 * Build a human-readable result string from recorded match data.
 * Uses matchData.result (if the engine set one) first, then falls back to
 * computing from innings totals — exactly as the previous HomePage helper did.
 * Returns a neutral string when data is incomplete; never invents a winner.
 */
export function getMatchResultText(match: any): string {
  const md = match?.matchData;

  // Prefer an engine-recorded result string when available
  if (typeof md?.result === "string" && md.result.trim()) return md.result;

  const firstInnings = md?.innings?.[0];
  const secondInnings = md?.innings?.[1];
  if (!firstInnings || !secondInnings) return "Match completed";
  const firstRuns = firstInnings.totalRuns ?? 0;
  const secondRuns = secondInnings.totalRuns ?? 0;
  const difference = Math.abs(firstRuns - secondRuns);
  const teamAName = match?.teamAName || "Team A";
  const teamBName = match?.teamBName || "Team B";
  if (firstRuns === secondRuns) return "Match tied";
  if (firstRuns > secondRuns) return `${teamAName} won by ${difference} runs`;
  const wicketsRemaining = 10 - (secondInnings.wickets ?? 0);
  return wicketsRemaining > 0
    ? `${teamBName} won by ${wicketsRemaining} wickets`
    : `${teamBName} won by ${difference} runs`;
}

export function getFormatLabel(format: string | undefined | null): string {
  if (!format) return "";
  switch (format.toUpperCase()) {
    case "INDOOR": return "Indoor";
    case "T20": return "T20";
    case "ODI": return "50 Overs";
    case "TEST": return "Test";
    default: return format;
  }
}
